import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from '../store/hooks';
import { projects } from '../store/data';
import type { Project } from '../types';

function ProjectModal({ project, onClose }: { project: Project; onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
        className="relative z-10 glass-card rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center text-white"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Image */}
        <div className="relative overflow-hidden rounded-t-2xl">
          <img
            src={project.image}
            alt={project.title}
            className="w-full aspect-video object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-surface-900/80 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <span className="inline-block px-3 py-1 text-xs font-body font-semibold bg-[#c8ff00] text-surface-900 rounded-full mb-3">
              {project.category}
            </span>
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">{project.title}</h3>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8 space-y-6">
          {/* Goal */}
          <div>
            <h4 className="font-body font-semibold text-[#c8ff00] text-sm uppercase tracking-wider mb-2">Project Goal</h4>
            <p className="font-body text-surface-300 leading-relaxed">{project.goal}</p>
          </div>

          {/* Full description */}
          <div>
            <h4 className="font-body font-semibold text-[#c8ff00] text-sm uppercase tracking-wider mb-2">The Story</h4>
            <p className="font-body text-surface-400 leading-relaxed">{project.fullDescription}</p>
          </div>

          {/* Technologies */}
          <div>
            <h4 className="font-body font-semibold text-[#c8ff00] text-sm uppercase tracking-wider mb-3">Technologies Used</h4>
            <div className="flex flex-wrap gap-2">
              {project.technologies.map((tech) => (
                <span
                  key={tech}
                  className="px-3 py-1.5 text-xs font-body font-medium text-surface-300 border border-white/10 rounded-full bg-white/[0.03]"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Portfolio() {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const { ref, isInView } = useInView(0.05);

  return (
    <>
      <section id="portfolio" ref={ref} className="py-28 sm:py-36 relative">
        <div className="max-w-7xl mx-auto px-6">
          {/* Section header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <span className="inline-block text-xs font-body font-semibold tracking-[0.25em] uppercase text-[#c8ff00] mb-4">
              Selected Work
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Projects that <span className="text-gradient italic">speak</span> for themselves
            </h2>
            <p className="font-body text-surface-400 mt-4 text-lg max-w-xl mx-auto">These websites are not affiliated with real companies; they are original designs created by us to demonstrate our capabilities and showcase the quality of our work. To respect client confidentiality and maintain strict privacy standards, we do not publish actual client projects on our website.</p>
          </motion.div>

          {/* Project grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, i) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1 + i * 0.1 }}
                onClick={() => setSelectedProject(project)}
                className="group cursor-pointer glass-card rounded-2xl overflow-hidden hover:border-[#c8ff00]/20 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#c8ff00]/5"
              >
                {/* Image */}
                <div className="relative overflow-hidden aspect-[16/10]">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-surface-900 via-surface-900/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />

                  {/* Category badge */}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 text-[11px] font-body font-semibold bg-white/10 backdrop-blur-md text-white rounded-full border border-white/10">
                      {project.category}
                    </span>
                  </div>

                  {/* Arrow icon */}
                  <div className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-[#c8ff00] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4 12L12 4M12 4H5M12 4V11" stroke="#0a0a0a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="font-display text-lg font-semibold text-white group-hover:text-[#c8ff00] transition-colors duration-300 mb-2">
                    {project.title}
                  </h3>
                  <p className="font-body text-sm text-surface-500 leading-relaxed line-clamp-3">
                    {project.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Project detail modal */}
      <AnimatePresence>
        {selectedProject && (
          <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
        )}
      </AnimatePresence>
    </>
  );
}
